import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

// ── Types ────────────────────────────────────────────────────────────────
type DummyTicketStatus =
  | "pending"
  | "contacted"
  | "confirmed"
  | "awaiting_payment"
  | "paid"
  | "delivered"
  | "cancelled";

interface DummyTicketItem {
  id: number;
  reference_code: string;
  trip_type: string;
  travelers_count: number;
  from_location: string;
  to_location: string;
  depart_date: string;
  return_date: string | null;
  full_name: string;
  passport_number: string;
  nationality: string;
  email: string;
  phone: string;
  notes: string | null;
  price: string | null;
  payment_link: string | null;
  payment_status: "unpaid" | "awaiting_payment" | "paid";
  admin_notes: string | null;
  ticket_file: string | null;
  status: DummyTicketStatus;
  created_at: string;
}

const STATUS_OPTIONS: { value: DummyTicketStatus; label: string }[] = [
  { value: "pending", label: "Pending Review" },
  { value: "contacted", label: "Contacted" },
  { value: "confirmed", label: "Confirmed" },
  { value: "awaiting_payment", label: "Awaiting Payment" },
  { value: "paid", label: "Paid" },
  { value: "delivered", label: "Delivered" },
  { value: "cancelled", label: "Cancelled" },
];

const STATUS_STYLES: Record<DummyTicketStatus, { bg: string; color: string; border: string }> = {
  pending: { bg: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  contacted: { bg: "#eff6ff", color: "#1e40af", border: "#bfdbfe" },
  confirmed: { bg: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  awaiting_payment: { bg: "#fefce8", color: "#a16207", border: "#fef08a" },
  paid: { bg: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  delivered: { bg: "#f0f9ff", color: "#0369a1", border: "#bae6fd" },
  cancelled: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

const StatusBadge = ({ status }: { status: DummyTicketStatus }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const label = STATUS_OPTIONS.find((o) => o.value === status)?.label || status;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: "20px",
        fontSize: "12px",
        fontWeight: 600,
        backgroundColor: s.bg,
        color: s.color,
        border: `1px solid ${s.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  );
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "9px 12px",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  fontSize: "13px",
  outline: "none",
  color: "#0f172a",
  backgroundColor: "#f8fafc",
  boxSizing: "border-box",
};

const labelStyle: React.CSSProperties = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#294b5a",
  letterSpacing: "0.04em",
  marginBottom: "5px",
  display: "block",
};

const primaryBtn: React.CSSProperties = {
  backgroundColor: "#294b5a",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "10px 18px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
};

const ghostBtn: React.CSSProperties = {
  backgroundColor: "#ffffff",
  color: "#294b5a",
  border: "1px solid #cbd5e1",
  borderRadius: "8px",
  padding: "10px 18px",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(15, 23, 42, 0.55)",
  backdropFilter: "blur(4px)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1050,
  padding: "16px",
};

const formatTripType = (t: string) => (t === "roundtrip" ? "Round Trip" : "One Way");

const ListDummyTicketRequests = () => {
  const [tickets, setTickets] = useState<DummyTicketItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const [selected, setSelected] = useState<DummyTicketItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [savingAdminFields, setSavingAdminFields] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);

  // Champs éditables dans le modal détails
  const [priceInput, setPriceInput] = useState("");
  const [ticketFileInput, setTicketFileInput] = useState("");
  const [notesInput, setNotesInput] = useState("");

  // Modal lien de paiement
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentLink, setPaymentLink] = useState("");
  const [paymentMessage, setPaymentMessage] = useState("");
  const [sendingPayment, setSendingPayment] = useState(false);

  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchTickets = useCallback(async (page = 1, status = statusFilter, search = "") => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/admin/dummy-ticket-requests`, {
            params: { page, status, search: search || undefined },
              ...config_token
      });
      setTickets(response.data.data || []);
      setCurrentPage(response.data.current_page || 1);
      setLastPage(response.data.last_page || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching dummy ticket requests:", error);
      toast.error("Error fetching dummy ticket requests");
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    fetchTickets(1, statusFilter, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Recherche avec debounce léger
  useEffect(() => {
    const t = setTimeout(() => {
      fetchTickets(1, statusFilter, searchTerm);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) {
      fetchTickets(page, statusFilter, searchTerm);
    }
  };

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  // ── Ouvrir le modal détails ──────────────────────────────────────────────
  const openDetails = async (id: number) => {
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/admin/dummy-ticket-requests/${id}`, config_token);
      const ticket: DummyTicketItem = response.data;
      setSelected(ticket);
      setPriceInput(ticket.price ? String(ticket.price) : "");
      setTicketFileInput(ticket.ticket_file || "");
      setNotesInput(ticket.admin_notes || "");
    } catch (error) {
      console.error("Error loading dummy ticket request:", error);
      toast.error("Error loading dummy ticket request details");
      setShowDetailsModal(false);
    } finally {
      setDetailsLoading(false);
    }
  };

  const closeDetails = () => {
    setShowDetailsModal(false);
    setSelected(null);
  };

  // ── Sauvegarder prix / fichier ticket / notes ────────────────────────────
  const handleSaveAdminFields = async () => {
    if (!selected) return;
    try {
      setSavingAdminFields(true);
     const response = await axios.patch( `${API_URL}/admin/dummy-ticket-requests/${selected.id}`,
     {
         price: priceInput || null,
         ticket_file: ticketFileInput || null,
          admin_notes: notesInput || null,
          },
            config_token
       );
      toast.success("Details saved");
      const updated = response.data.data;
      setSelected(updated);
      setTickets((prev) => prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
    } catch (error) {
      console.error("Error saving fields:", error);
      toast.error("Error saving details");
    } finally {
      setSavingAdminFields(false);
    }
  };

  // ── Changer le statut ─────────────────────────────────────────────────────
  const handleStatusChange = async (newStatus: DummyTicketStatus) => {
    if (!selected) return;
    try {
      setChangingStatus(true);
      const response = await axios.patch(`${API_URL}/admin/dummy-ticket-requests/${selected.id}/status`, {
        status: newStatus,
      }, config_token);
      toast.success("Status updated");
      const updated = response.data.data;
      setSelected(updated);
      setTickets((prev) => prev.map((f) => (f.id === updated.id ? { ...f, status: updated.status } : f)));
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
    } finally {
      setChangingStatus(false);
    }
  };

  // ── Suppression ───────────────────────────────────────────────────────────
  const handleDeleteClick = (ticket: DummyTicketItem) => {
    setSelected(ticket);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API_URL}/admin/dummy-ticket-requests/${selected.id}` , config_token);
      toast.success("Dummy ticket request deleted");
      setTickets((prev) => prev.filter((f) => f.id !== selected.id));
      setShowDeleteModal(false);
      setShowDetailsModal(false);
      setSelected(null);
    } catch (error) {
      console.error("Error deleting dummy ticket request:", error);
      toast.error("Error deleting dummy ticket request");
    }
  };

  // ── Lien de paiement ─────────────────────────────────────────────────────
  const openPaymentModal = (ticket: DummyTicketItem) => {
    setSelected(ticket);
    setPaymentAmount(ticket.price ? String(ticket.price) : "");
    setPaymentLink(ticket.payment_link || "");
    setPaymentMessage("");
    setShowPaymentModal(true);
  };

  const handleSendPaymentLink = async () => {
    if (!selected) return;
    if (!paymentAmount || !paymentLink.trim()) {
      toast.error("Amount and payment link are required");
      return;
    }
    try {
      setSendingPayment(true);
      const response = await axios.post(`${API_URL}/admin/dummy-ticket-requests/${selected.id}/send-payment-link`, {
        amount: paymentAmount,
        payment_link: paymentLink,
        message: paymentMessage || undefined,
      }, config_token);
      toast.success("Payment link saved");
      const updated = response.data.data;
      setTickets((prev) => prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
      if (selected) setSelected({ ...selected, ...updated });
      setShowPaymentModal(false);
    } catch (error) {
      console.error("Error sending payment link:", error);
      toast.error("Error sending payment link");
    } finally {
      setSendingPayment(false);
    }
  };

  const handleMarkPaid = async (ticket: DummyTicketItem) => {
    try {
      const response = await axios.post(`${API_URL}/admin/dummy-ticket-requests/${ticket.id}/mark-paid`, config_token);
      toast.success("Marked as paid");
      const updated = response.data.data;
      setTickets((prev) => prev.map((f) => (f.id === updated.id ? { ...f, ...updated } : f)));
      if (selected?.id === updated.id) setSelected({ ...selected, ...updated });
    } catch (error) {
      console.error("Error marking as paid:", error);
      toast.error("Error updating payment status");
    }
  };

  const clearSearch = () => setSearchTerm("");

  // ── Skeleton Loader Component ──────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      {/* Table Header Skeleton */}
      <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
        <div className="row">
          {["Reference", "Traveler", "Route", "Departure", "Pax", "Status", "Actions"].map((_, i) => (
            <div key={i} className="col" style={{ padding: "0 8px" }}>
              <div style={{
                width: i === 0 ? 70 : i === 1 ? 80 : i === 2 ? 80 : i === 3 ? 70 : i === 4 ? 40 : i === 5 ? 60 : 50,
                height: 14,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          ))}
        </div>
      </div>

      {/* Table Rows Skeleton */}
      {[...Array(8)].map((_, rowIndex) => (
        <div
          key={rowIndex}
          style={{
            padding: "14px 16px",
            borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
            display: "flex",
            alignItems: "center"
          }}
        >
          {[...Array(7)].map((_, colIndex) => (
            <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
              <div style={{
                width: colIndex === 0 ? 100 : colIndex === 1 ? 140 : colIndex === 2 ? 120 : colIndex === 3 ? 100 : colIndex === 4 ? 40 : colIndex === 5 ? 80 : 70,
                height: colIndex === 1 ? 16 : 14,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 4,
                animation: "shimmer 1.5s infinite"
              }}></div>
              {colIndex === 1 && (
                <div style={{
                  width: 120,
                  height: 12,
                  marginTop: 4,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 3,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              )}
            </div>
          ))}
        </div>
      ))}

      {/* Pagination Skeleton */}
      <div style={{
        padding: "12px 20px",
        borderTop: "1px solid #edf2f7",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center"
      }}>
        <div style={{
          width: 200,
          height: 16,
          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
          backgroundSize: "200% 100%",
          borderRadius: 4,
          animation: "shimmer 1.5s infinite"
        }}></div>
        <div style={{ display: "flex", gap: "6px" }}>
          {[...Array(5)].map((_, i) => (
            <div key={i} style={{
              width: i === 0 || i === 4 ? 70 : 32,
              height: 32,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
          ))}
        </div>
      </div>
    </div>
  );
}

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>
              Dummy Ticket Requests
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
                letterSpacing: "0.5px",
              }}
            >
              System
            </span>
          </div>
          <p style={{ color: "#64748b", fontSize: "13.5px", margin: 0, fontWeight: 400 }}>
            <strong style={{ color: "#294b5a" }}>{total}</strong> dummy ticket request{total !== 1 ? "s" : ""} total.
          </p>
        </div>
      </div>

      {/* BLOC PRINCIPAL */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7", backgroundColor: "#ffffff" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by reference, name, email, route..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ ...inputStyle, padding: "9px 36px 9px 38px" }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", fontSize: "13px", color: "#94a3b8" }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8" }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>
            <div className="col-12 col-md-4 col-lg-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{ ...inputStyle, cursor: "pointer" }}
              >
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
         <SkeletonLoader/>
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Reference</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Traveler</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Route</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Departure</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Pax</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Status</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.length > 0 ? (
                    tickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        style={{ borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#fafaf8")}
                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        onClick={() => openDetails(ticket.id)}
                      >
                        <td style={{ padding: "14px 16px", fontSize: "13px", fontWeight: 700, color: "#294b5a" }}>
                          {ticket.reference_code}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#0f172a" }}>
                          {ticket.full_name}
                          <div style={{ fontSize: "11.5px", color: "#94a3b8" }}>{ticket.email}</div>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                          {ticket.from_location} → {ticket.to_location}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569" }}>
                          {new Date(ticket.depart_date).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "13px", color: "#475569" }}>
                          {ticket.travelers_count}
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <StatusBadge status={ticket.status} />
                        </td>
                        <td style={{ padding: "14px 16px", textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            <button
                              type="button"
                              onClick={() => openDetails(ticket.id)}
                              title="View details"
                              style={{ border: "1px solid #e2e8f0", backgroundColor: "#ffffff", color: "#294b5a", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-eye" style={{ fontSize: "13px" }}></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => openPaymentModal(ticket)}
                              title="Send payment link"
                              style={{ border: "1px solid #e2e8f0", backgroundColor: "#ffffff", color: "#a16207", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-money-check-dollar" style={{ fontSize: "13px" }}></i>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(ticket)}
                              title="Delete"
                              style={{ border: "1px solid #e2e8f0", backgroundColor: "#ffffff", color: "#64748b", width: "32px", height: "32px", borderRadius: "6px", cursor: "pointer" }}
                            >
                              <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: "13.5px" }}>
                        <i className="fa-solid fa-ticket mb-2" style={{ fontSize: "24px", color: "#cbd5e1", display: "block" }}></i>
                        No dummy ticket requests found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {tickets.length > 0 && lastPage > 1 && (
              <div
                style={{
                  padding: "12px 20px",
                  borderTop: "1px solid #edf2f7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "10px",
                }}
              >
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    style={{ padding: "6px 12px", fontSize: "12.5px", fontWeight: 600, border: "1px solid #cbd5e1", borderRadius: "6px", backgroundColor: "#fff", color: currentPage === 1 ? "#94a3b8" : "#294b5a", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}
                  >
                    Previous
                  </button>
                  {getPaginationRange(currentPage, lastPage).map((item, index) =>
                    item === "..." ? (
                      <span key={`dots-${index}`} style={{ padding: "0 6px", fontSize: "13px", color: "#94a3b8", fontWeight: 600 }}>...</span>
                    ) : (
                      <button
                        key={item}
                        onClick={() => handlePageChange(item as number)}
                        style={{
                          width: "32px", height: "32px", fontSize: "12.5px", fontWeight: 600, borderRadius: "6px",
                          border: currentPage === item ? "none" : "1px solid #cbd5e1",
                          backgroundColor: currentPage === item ? "#294b5a" : "#fff",
                          color: currentPage === item ? "#fff" : "#334155",
                          cursor: "pointer",
                        }}
                      >
                        {item}
                      </button>
                    )
                  )}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === lastPage}
                    style={{ padding: "6px 12px", fontSize: "12.5px", fontWeight: 600, border: "1px solid #cbd5e1", borderRadius: "6px", backgroundColor: "#fff", color: currentPage === lastPage ? "#94a3b8" : "#294b5a", cursor: currentPage === lastPage ? "not-allowed" : "pointer" }}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODAL DÉTAILS ── */}
      {showDetailsModal && (
        <div style={modalOverlay} onClick={closeDetails}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: "#fff", borderRadius: "16px", width: "100%", maxWidth: "760px", maxHeight: "88vh", overflowY: "auto", border: "1px solid #e2e8f0" }}
          >
            {detailsLoading || !selected ? (
              <div style={{ padding: "60px", textAlign: "center", color: "#64748b" }}>
                <i className="fa-solid fa-circle-notch fa-spin me-2" style={{ color: "#294b5a" }}></i>
                Loading...
              </div>
            ) : (
              <>
                <div style={{ padding: "20px 24px", borderBottom: "1px solid #edf2f7", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: 0 }}>
                      {selected.reference_code}
                    </h2>
                    <div style={{ marginTop: "6px" }}><StatusBadge status={selected.status} /></div>
                  </div>
                  <button onClick={closeDetails} style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", fontSize: "18px" }}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </div>

                <div style={{ padding: "24px" }}>
                  {/* Change status */}
                  <div style={{ marginBottom: "20px" }}>
                    <label style={labelStyle}>Change Status</label>
                    <select
                      value={selected.status}
                      disabled={changingStatus}
                      onChange={(e) => handleStatusChange(e.target.value as DummyTicketStatus)}
                      style={{ ...inputStyle, maxWidth: "280px", cursor: "pointer" }}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="row g-3">
                    <div className="col-6">
                      <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9c9488", marginBottom: "8px" }}>TRIP</div>
                        <div style={{ fontSize: "13px", color: "#0f172a" }}>
                          {selected.from_location} → {selected.to_location}
                        </div>
                        <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "4px" }}>
                          {new Date(selected.depart_date).toLocaleDateString()} {selected.return_date && `→ ${new Date(selected.return_date).toLocaleDateString()}`}
                        </div>
                        <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "2px" }}>
                          {formatTripType(selected.trip_type)} · {selected.travelers_count} traveler{selected.travelers_count > 1 ? "s" : ""}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9c9488", marginBottom: "8px" }}>TRAVELER</div>
                        <div style={{ fontSize: "13px", color: "#0f172a" }}>{selected.full_name}</div>
                        <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "4px" }}>{selected.email} · {selected.phone}</div>
                        <div style={{ fontSize: "12.5px", color: "#64748b", marginTop: "2px" }}>
                          {selected.nationality} · Passport {selected.passport_number}
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9c9488", marginBottom: "8px" }}>PAYMENT</div>
                        <div style={{ fontSize: "13px", color: "#0f172a" }}>
                          {selected.price ? `$${selected.price}` : "Not set"} · {(selected.payment_status || "unpaid").replace("_", " ")}
                        </div>
                        {selected.payment_status !== "paid" && selected.price && (
                          <button
                            onClick={() => handleMarkPaid(selected)}
                            style={{ marginTop: "8px", fontSize: "11.5px", fontWeight: 600, color: "#047857", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                          >
                            Mark as paid
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="col-6">
                      <div style={{ background: "#f8fafc", borderRadius: "10px", padding: "14px 16px", border: "1px solid #e2e8f0" }}>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#9c9488", marginBottom: "8px" }}>TICKET FILE</div>
                        <div style={{ fontSize: "13px", color: "#0f172a", wordBreak: "break-all" }}>
                          {selected.ticket_file ? (
                            <a href={selected.ticket_file} target="_blank" rel="noreferrer" style={{ color: "#294b5a" }}>
                              {selected.ticket_file}
                            </a>
                          ) : (
                            "Not issued yet"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {selected.notes && (
                    <div style={{ marginTop: "16px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "10px", padding: "12px 16px" }}>
                      <div style={{ fontSize: "11px", fontWeight: 700, color: "#92400e", marginBottom: "4px" }}>TRAVELER NOTES</div>
                      <div style={{ fontSize: "13px", color: "#78350f" }}>{selected.notes}</div>
                    </div>
                  )}

                  {/* Champs gérés par l'admin */}
                  <div style={{ marginTop: "22px", borderTop: "1px solid #edf2f7", paddingTop: "18px" }}>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "#294b5a", marginBottom: "12px" }}>Admin Fields</div>
                    <div className="row g-3">
                      <div className="col-6">
                        <label style={labelStyle}>Price ($)</label>
                        <input style={inputStyle} type="number" min="0" step="0.01" value={priceInput} onChange={(e) => setPriceInput(e.target.value)} placeholder="e.g. 45.00" />
                      </div>
                      <div className="col-6">
                        <label style={labelStyle}>Ticket File (URL / path)</label>
                        <input style={inputStyle} type="text" value={ticketFileInput} onChange={(e) => setTicketFileInput(e.target.value)} placeholder="e.g. /storage/tickets/DT-AB12CD.pdf" />
                      </div>
                      <div className="col-12">
                        <label style={labelStyle}>Internal Notes</label>
                        <textarea style={{ ...inputStyle, minHeight: "70px", resize: "vertical" }} value={notesInput} onChange={(e) => setNotesInput(e.target.value)} placeholder="Notes visible to admins only" />
                      </div>
                    </div>
                    <div style={{ marginTop: "12px" }}>
                      <button onClick={handleSaveAdminFields} disabled={savingAdminFields} style={ghostBtn}>
                        {savingAdminFields ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                </div>

                <div style={{ padding: "16px 24px", borderTop: "1px solid #edf2f7", display: "flex", gap: "10px", justifyContent: "space-between", flexWrap: "wrap" }}>
                  <button onClick={() => handleDeleteClick(selected)} style={{ ...ghostBtn, color: "#dc2626", borderColor: "#fecaca" }}>
                    <i className="fa-solid fa-trash-can" style={{ marginRight: 6 }}></i>Delete
                  </button>
                  <div style={{ display: "flex", gap: "10px" }}>
                    <button onClick={() => openPaymentModal(selected)} style={primaryBtn}>
                      <i className="fa-solid fa-money-check-dollar"></i>Payment Link
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL DELETE ── */}
      {showDeleteModal && selected && (
        <div style={modalOverlay} onClick={() => setShowDeleteModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "#fff", borderRadius: "16px", width: "100%", maxWidth: "440px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "28px 24px 20px", textAlign: "center" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "#fef2f2", border: "1px solid #fecaca", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>
              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px" }}>Delete dummy ticket request?</h2>
              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0 }}>This action cannot be undone.</p>
              <div style={{ marginTop: "16px", padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "left" }}>
                <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>{selected.reference_code}</div>
                <div style={{ fontSize: "12px", color: "#64748b" }}>{selected.full_name} · {selected.from_location} → {selected.to_location}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px", padding: "16px 24px 20px" }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleConfirmDelete} style={{ flex: 1, padding: "10px 16px", fontSize: "13.5px", fontWeight: 600, color: "#fff", backgroundColor: "#dc2626", border: "none", borderRadius: "8px", cursor: "pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL PAYMENT LINK ── */}
      {showPaymentModal && selected && (
        <div style={modalOverlay} onClick={() => setShowPaymentModal(false)}>
          <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: "#fff", borderRadius: "16px", width: "100%", maxWidth: "480px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #edf2f7" }}>
              <h2 style={{ fontSize: "16px", fontWeight: 700, color: "#294b5a", margin: 0 }}>Send payment link</h2>
              <p style={{ fontSize: "12.5px", color: "#94a3b8", margin: "2px 0 0" }}>{selected.reference_code} · {selected.email}</p>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Amount ($)</label>
                <input style={inputStyle} type="number" min="0" step="0.01" value={paymentAmount} onChange={(e) => setPaymentAmount(e.target.value)} placeholder="e.g. 45.00" />
              </div>
              <div style={{ marginBottom: "14px" }}>
                <label style={labelStyle}>Payment Link</label>
                <input style={inputStyle} type="url" value={paymentLink} onChange={(e) => setPaymentLink(e.target.value)} placeholder="https://checkout.stripe.com/..." />
              </div>
              <div>
                <label style={labelStyle}>Note (optional)</label>
                <textarea style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }} value={paymentMessage} onChange={(e) => setPaymentMessage(e.target.value)} placeholder="Add context for the client..." />
              </div>
              <p style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "10px", marginBottom: 0 }}>
                Note: unlike flight requests, this only saves the amount and link on the record — the backend's{" "}
                <code>sendPaymentLink</code> for dummy tickets doesn't send an email yet (the <code>Mail::send</code> call is
                commented out pending a <code>DummyTicketPaymentLinkMail</code> Mailable).
              </p>
            </div>
            <div style={{ display: "flex", gap: "12px", padding: "16px 24px 20px" }}>
              <button onClick={() => setShowPaymentModal(false)} style={{ ...ghostBtn, flex: 1 }}>Cancel</button>
              <button onClick={handleSendPaymentLink} disabled={sendingPayment} style={{ ...primaryBtn, flex: 1, justifyContent: "center", background: "linear-gradient(90deg,#d3be76,#b09a50)", color: "#1a3240" }}>
                {sendingPayment ? "Saving..." : "Save Payment Link"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListDummyTicketRequests;